import {
  first_value
} from '@/services/onboarding_form_helpers';
import {
  aggregate_file_saved_answers,
  apply_saved_answer_to_question,
  build_saved_answers_maps,
  normalize_answer_group_key,
  resolve_section_question_id
} from './queue_form_saved_answer_helpers';

function build_question_state_key(section_question_id, answer_group_key = null) {
  const state_key = answer_group_key
    ? `${section_question_id}::${answer_group_key}`
    : String(section_question_id);
  return state_key;
}

function clone_question_for_runtime(question, answer_group_key = null) {
  const section_question_id = resolve_section_question_id(question);
  const runtime_question = {
    ...question,
    section_question_id,
    id: build_question_state_key(section_question_id, answer_group_key),
    answer_group_key,
    dependent_questions: (Array.isArray(question?.dependent_questions) ? question.dependent_questions : [])
      .map((dependent_question) => clone_question_for_runtime(dependent_question, answer_group_key))
    };
  return runtime_question;
}

function clone_sub_section_for_runtime(sub_section, answer_group_key = null, instance_number = 1) {
  const base_section_id = first_value([sub_section?.base_section_id, sub_section?.id], null);
  const runtime_sub_section = {
    ...sub_section,
    base_section_id,
    answer_group_key,
    instance_number,
    instance_id: `${base_section_id || 'section'}::${answer_group_key || 'base'}::${instance_number}`,
    questions: (Array.isArray(sub_section?.questions) ? sub_section.questions : [])
      .map((question) => clone_question_for_runtime(question, answer_group_key))
  };
  return runtime_sub_section;
}

function find_saved_answer_for_question({
  question,
  answer_group_key = null,
  saved_answers_by_question_id,
  saved_answers_by_question_label
}) {
  const answers_by_id = saved_answers_by_question_id.get(question.section_question_id) || [];
  const normalized_group_key = normalize_answer_group_key(answer_group_key);
  const id_matches = answers_by_id.filter((answer) => (
    normalize_answer_group_key(answer.answer_group_key) === normalized_group_key
  ));
  if (id_matches.length) {
    return question.type === 'file'
      ? aggregate_file_saved_answers(id_matches)
      : id_matches[0];
  }
  if (answers_by_id.length === 1 && !normalized_group_key) return answers_by_id[0];

  const normalized_question_label = String(question.label || '').trim().toLowerCase();
  const answers_by_label = saved_answers_by_question_label.get(normalized_question_label) || [];
  const label_matches = answers_by_label.filter((answer) => (
    normalize_answer_group_key(answer.answer_group_key) === normalized_group_key
  ));
  if (label_matches.length) {
    return question.type === 'file'
      ? aggregate_file_saved_answers(label_matches)
      : label_matches[0];
  }
  return !normalized_group_key && answers_by_label.length === 1 ? answers_by_label[0] : null;
}

function collect_sub_section_group_keys(sub_section, saved_answers_by_question_id) {
  const grouped_keys = [];
  let has_ungrouped_answers = false;

  (Array.isArray(sub_section?.questions) ? sub_section.questions : []).forEach((question) => {
    const answers = saved_answers_by_question_id.get(resolve_section_question_id(question)) || [];
    answers.forEach((answer) => {
      const answer_group_key = normalize_answer_group_key(answer.answer_group_key);
      if (!answer_group_key) {
        has_ungrouped_answers = true;
        return;
      }
      if (!grouped_keys.includes(answer_group_key)) grouped_keys.push(answer_group_key);
    });
  });

  const group_keys = { grouped_keys, has_ungrouped_answers };
  return group_keys;
}

function apply_saved_answers_to_sub_section({
  sub_section,
  answer_group_key = null,
  saved_answers_by_question_id,
  saved_answers_by_question_label,
  persisted_file_urls
}) {
  function apply_to_question(question) {
    const saved_answer = find_saved_answer_for_question({
      question,
      answer_group_key,
      saved_answers_by_question_id,
      saved_answers_by_question_label
    });
    apply_saved_answer_to_question({ question, saved_answer, persisted_file_urls });
    (Array.isArray(question?.dependent_questions) ? question.dependent_questions : []).forEach(apply_to_question);
  }

  sub_section.questions.forEach(apply_to_question);
  return sub_section;
}

function materialize_sub_section_instances({
  sub_section,
  saved_answers_by_question_id,
  saved_answers_by_question_label,
  persisted_file_urls
}) {
  const group_keys = collect_sub_section_group_keys(
    sub_section,
    saved_answers_by_question_id
  );
  const grouped_keys = group_keys.grouped_keys;
  const has_ungrouped_answers = group_keys.has_ungrouped_answers;

  if (!grouped_keys.length) {
    const single_instance = apply_saved_answers_to_sub_section({
      sub_section: clone_sub_section_for_runtime(sub_section, null, 1),
      answer_group_key: null,
      saved_answers_by_question_id,
      saved_answers_by_question_label,
      persisted_file_urls
    });
    return [single_instance];
  }

  const instance_keys = has_ungrouped_answers ? [null, ...grouped_keys] : grouped_keys;
  const materialized_instances = instance_keys.map((answer_group_key, index) => {
    const sub_section_instance = apply_saved_answers_to_sub_section({
      sub_section: clone_sub_section_for_runtime(sub_section, answer_group_key, index + 1),
      answer_group_key,
      saved_answers_by_question_id,
      saved_answers_by_question_label,
      persisted_file_urls
    });
    return sub_section_instance;
  });
  return materialized_instances;
}

export function materialize_repeatable_steps({
  steps = [],
  extracted_saved_answers = [],
  persisted_file_urls = {}
}) {
  const answer_maps = build_saved_answers_maps(extracted_saved_answers);
  const byQuestionId = answer_maps.byQuestionId;
  const byQuestionLabel = answer_maps.byQuestionLabel;

  const runtime_steps = steps.map((step) => {
    const runtime_step = {
      ...step,
      sub_sections: (Array.isArray(step?.sub_sections) ? step.sub_sections : [])
        .flatMap((sub_section) => materialize_sub_section_instances({
          sub_section,
          saved_answers_by_question_id: byQuestionId,
          saved_answers_by_question_label: byQuestionLabel,
          persisted_file_urls
        }))
    };
    return runtime_step;
  });
  return runtime_steps;
}

export function create_runtime_sub_section_instance(sub_section, answer_group_key, instance_number = 1) {
  const runtime_sub_section = clone_sub_section_for_runtime(
    sub_section,
    normalize_answer_group_key(answer_group_key),
    instance_number
  );
  return runtime_sub_section;
}

export function create_blank_runtime_sub_section_instance(sub_section, answer_group_key, instance_number = 1) {
  const runtime_sub_section = create_runtime_sub_section_instance(
    sub_section,
    answer_group_key,
    instance_number
  );

  function reset_question_answer(question) {
    const reset_question = {
      ...question,
      answer: question.type === 'multi_select' ? [] : '',
      dependent_questions: (Array.isArray(question?.dependent_questions) ? question.dependent_questions : [])
        .map(reset_question_answer)
    };
    return reset_question;
  }

  const blank_runtime_sub_section = {
    ...runtime_sub_section,
    questions: runtime_sub_section.questions.map(reset_question_answer)
  };
  return blank_runtime_sub_section;
}
